import { useQuery } from '@tanstack/react-query'
import { useCompanion } from '@/hooks/useCompanion'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'
import { supabase } from '@/lib/supabase'
import { ageMonths } from '@/lib/types'
import { ageBucketFor } from '@/lib/rhythm/blocks'
import { computeMeters } from '@/lib/rhythm/sicknessMeters'
import { loadCompanionEvidence, pairedSleepEvidence } from '@/lib/companion'
import { RhythmMeterWhy } from '@/components/RhythmMeterWhy'

/** Read-only reuse of the existing rhythm computation. Today does not currently
 * mount RhythmPlan; importing that whole WIP surface would activate unrelated
 * weather calls and completion writers. This small flagged row does neither. */
export function CompanionRhythmMeters() {
  const { scope, enabled } = useCompanion()
  const person = useFocusedPerson()
  const bucket = person && person.person_type !== 'adult' ? ageBucketFor(ageMonths(person)) : null
  const query = useQuery({
    enabled: enabled && !!scope && !!bucket,
    queryKey: ['companion-meter-records', scope?.userId, scope?.familyId, scope?.personId],
    staleTime: 0, gcTime: 0, retry: false,
    queryFn: async () => {
      if (!scope) throw new Error('No active context')
      const evidence = await loadCompanionEvidence(scope)
      const since = new Date(evidence.end); since.setHours(0, 0, 0, 0)
      const { data, error } = await supabase.from('rhythm_completions')
        .select('block_type, minutes_actual, completed_at')
        .eq('family_id', scope.familyId).eq('person_id', scope.personId)
        .gte('completed_at', since.toISOString()).lte('completed_at', evidence.end).limit(301)
      if (error || !data || data.length > 300) throw new Error('Completion records unavailable or truncated')
      return { evidence, completions: data as Array<{ block_type: string; minutes_actual: number | null; completed_at: string }> }
    },
  })
  if (!enabled || !scope || !bucket) return null
  const paired = query.data ? pairedSleepEvidence(query.data.evidence) : null
  const meters = computeMeters({ bucket, completionsToday: query.data?.completions ?? [], sleepHoursLast24h: (paired?.minutes ?? 0) / 60 })
  return (
    <section aria-label="Companion rhythm meters" className="rounded-r-sm border border-divider-subtle p-3 space-y-2">
      <h2 className="text-caption text-text-secondary">Today's rhythm · experimental</h2>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {meters.map((meter) => {
          const known = !!query.data && !query.isError && (meter.kind !== 'sleep_adequacy' || !!paired)
          return <div key={meter.kind} className="text-center text-caption text-text-secondary">
            <div>{meter.label}</div>
            {known && <progress aria-label={`${meter.label} recorded progress`} max={1} value={meter.progress} className="w-full" />}
            <div>{!known ? 'Records unavailable' : meter.kind === 'vitamin_d'
              ? (meter.actual_min ? 'Marked logged' : 'Not marked')
              : `${meter.actual_min} / ${meter.target_min} min`}</div>
            <RhythmMeterWhy meter={meter} personId={scope.personId} />
          </div>
        })}
      </div>
      <p className="text-caption text-text-secondary">Existing rhythm-rule targets, not medical adequacy. Completion-based meters lack event citations; Why discloses this rather than inventing evidence. Unsynced records may be missing.</p>
    </section>
  )
}
